import type {
    DatapointOptions,
    LogUsageFunction,
    LogGreyErrorFunction,
    CustomDataMap,
    LogCoreUsageFunction,
    LogCoreGreyErrorFunction,
} from 'owa-analytics-types';
import type { AnalyticsEventNames, AnalyticsCoreEventNames } from 'owa-analytics-events';
import type { MailboxInfo } from 'owa-client-types';

export interface StartUsage<TKeys extends string = string> {
    name: AnalyticsEventNames | AnalyticsCoreEventNames;
    mailbox?: MailboxInfo;
    customData?: CustomDataMap<TKeys>;
    options?: DatapointOptions;
}

export interface StartGreyError extends StartUsage {
    error?: Error;
}

let logUsageFunction: LogUsageFunction | undefined;
let logCoreUsageFunction: LogCoreUsageFunction | undefined;
let logGreyErrorFunction: LogGreyErrorFunction | undefined;
let logCoreGreyErrorFunction: LogCoreGreyErrorFunction | undefined;
const cachedStartUsage: StartUsage[] = [];
const cachedStartCoreUsage: StartUsage[] = [];
const cachedStartGreyError: (StartUsage & {
    error?: Error;
})[] = [];
const cachedStartCoreGreyError: (StartUsage & {
    error?: Error;
})[] = [];

export function logStartUsage<TKeys extends string = string>(
    name: AnalyticsEventNames,
    mailbox: MailboxInfo | undefined,
    customData?: CustomDataMap<TKeys>,
    options?: DatapointOptions
): void {
    if (logUsageFunction) {
        logUsageFunction(name, mailbox, customData, options);
    } else {
        cachedStartUsage.push({ name, mailbox, customData, options });
    }
}

export function logStartCoreUsage<TKeys extends string = string>(
    name: AnalyticsCoreEventNames,
    mailbox: MailboxInfo | undefined,
    customData?: CustomDataMap<TKeys>,
    options?: DatapointOptions
): void {
    if (logCoreUsageFunction) {
        logCoreUsageFunction(name, mailbox, customData, options);
    } else {
        cachedStartCoreUsage.push({ name, mailbox, customData, options });
    }
}

export function logStartGreyError<TKeys extends string = string>(
    name: AnalyticsEventNames,
    mailbox: MailboxInfo | undefined,
    error?: Error,
    customData?: CustomDataMap<TKeys>,
    options?: DatapointOptions
): void {
    if (logGreyErrorFunction) {
        logGreyErrorFunction(name, mailbox, error, customData, options);
    } else {
        cachedStartGreyError.push({ name, mailbox, error, customData, options });
    }
}

export function logStartCoreGreyError<TKeys extends string = string>(
    name: AnalyticsCoreEventNames,
    mailbox: MailboxInfo | undefined,
    error?: Error,
    customData?: CustomDataMap<TKeys>,
    options?: DatapointOptions
): void {
    if (logCoreGreyErrorFunction) {
        logCoreGreyErrorFunction(name, mailbox, error, customData, options);
    } else {
        cachedStartCoreGreyError.push({ name, mailbox, error, customData, options });
    }
}

export function initLogUsageFunc(func: LogUsageFunction): StartUsage[] {
    logUsageFunction = func;
    return cachedStartUsage;
}

export function initCoreLogUsageFunc(func: LogCoreUsageFunction): StartUsage[] {
    logCoreUsageFunction = func;
    return cachedStartCoreUsage;
}

export function initLogGreyErrorFunc(func: LogGreyErrorFunction): StartGreyError[] {
    logGreyErrorFunction = func;
    return cachedStartGreyError;
}

export function initCoreLogGreyErrorFunc(func: LogCoreGreyErrorFunction): StartGreyError[] {
    logCoreGreyErrorFunction = func;
    return cachedStartCoreGreyError;
}
